/**
 * Layout primitives for the new-job form. CSS vars `--col` and `--row` on
 * `JobFormBody` are inherited by descendants, so individual fields reference
 * `var(--col)` / `var(--row)` without carrying pixel values. To resize, edit
 * the `clamp()` on `--col` below; to change the multi-column un-collapse
 * point, edit `FORM_COLLAPSE_PX`.
 *
 * Multi-column repeating rows (data mount, dns port, etc.) declare their own
 * grids locally — column shapes differ row to row, so a shared abstraction
 * isn't useful.
 */
import { Button, Container, styled, Typography } from "@mui/material";
import type { ButtonProps } from "@mui/material";
import React from "react";

/** Viewport width at which multi-column rows un-collapse to a single line. */
export const FORM_COLLAPSE_PX = 1200;

/**
 * Top-level form wrapper. Defines `--col` (column width, clamped 320–600px)
 * and `--row` (two columns + gap). `minWidth: 760` keeps the form at its
 * natural 2-column size; narrower viewports get a horizontal scrollbar from
 * the surrounding `main`.
 */
export const JobFormBody = styled(Container)(({ theme }) => ({
  "--col": "clamp(320px, 40vw, 600px)",
  "--row": `calc(var(--col) * 2 + ${theme.spacing(1)})`,
  // Cap the whole form at `--row` so step headers, summaries, and content
  // align in one column. Selector specificity beats MUI's built-in lg cap.
  "&.MuiContainer-root": {
    maxWidth: "var(--row)",
  },
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8),
  minWidth: 760,
}));

/**
 * Footer for Cancel + Launch. Uses MUI Container's default `lg` width
 * (matching other sticky footers in the app), not JobFormBody's `--row` cap.
 */
export const JobFormFooter = styled(Container)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  "& button": {
    whiteSpace: "nowrap",
  },
}));

/**
 * Row of two fields with an optional action button (e.g. a clear button)
 * pinned to the right. Children fill the grid in document order, so just
 * render the two inputs then optionally the button — no need to set
 * gridColumn anywhere.
 */
export const FieldRow = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(0, var(--col)) minmax(0, var(--col)) auto",
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(1.5),
  alignItems: "start",
  "& .clear": { marginTop: 6 },
}));

/** Single field sized to one column. Spread onto a sx/style prop when no wrapper. */
export const fieldStyle = {
  width: "100%",
  maxWidth: "var(--col)",
} as const;

/**
 * Single full-row control (e.g. working directory, command, yaml preview).
 */
export const wideFieldStyle = {
  width: "100%",
  maxWidth: "var(--row)",
} as const;

const StyledAddRowButton = styled(Button)<{ hasError?: boolean }>(
  ({ theme, hasError }) => ({
    ...wideFieldStyle,
    height: theme.spacing(7),
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: hasError ? theme.palette.error.main : theme.palette.grey[500],
    justifyContent: "start",
    color: hasError ? theme.palette.error.main : undefined,
  }),
);

const ErrorCaption = styled(Typography)(({ theme }) => ({
  ...wideFieldStyle,
  marginTop: theme.spacing(0.5),
  marginLeft: theme.spacing(1.75),
}));

type AddRowButtonProps = ButtonProps & {
  /**
   * If set, renders the button with error styling and shows the message
   * below — used when ≥1 row is required but none exists yet.
   */
  error?: string | null;
};

/** "+ Add ..." dashed button at the end of repeating sections. */
export const AddRowButton = React.forwardRef<
  HTMLButtonElement,
  AddRowButtonProps
>(function AddRowButton({ error, ...props }, ref) {
  return (
    <div>
      <StyledAddRowButton ref={ref} hasError={Boolean(error)} {...props} />
      {error ? (
        <ErrorCaption variant="caption" color="error">
          {error}
        </ErrorCaption>
      ) : null}
    </div>
  );
});
